import {useEntitlement} from '@github-ui/copilot-chat/components/quota/EntitlementContext'
import {CopilotPlan} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
import {useAgentModels} from '../../hooks/useAgentModels'
import {ModelPicker as AgentModelPicker} from '../task-view/ModelPicker'
import {useLastSelectedAgent} from '../../hooks/use-last-selected-agent'
import {agentSupportsModelSelection} from '../../utils/agent-helpers'
import {useAgentVendorModel} from '../../hooks/use-agent-vendor-model'
import type {SelectedAgent} from '../../types/session'

interface SlashCommandModelPickerProps {
  /**
   * The currently selected agent, passed from the parent ChatInput context.
   * This ensures the model picker updates reactively when the agent selection changes.
   */
  selectedAgent?: SelectedAgent
}

/**
 * Model picker component for the /task slash command.
 * This wraps the base ModelPicker with state management via useLatestAgentModel hook
 * and adds feature flag checks for when the model picker should be shown.
 */
export function SlashCommandModelPicker({selectedAgent: selectedAgentProp}: SlashCommandModelPickerProps) {
  const {plan, tokenBasedBillingEnabled} = useEntitlement()
  const {models: agentModels} = useAgentModels()
  const [selectedAgentFromStorage] = useLastSelectedAgent()
  const selectedAgent = selectedAgentProp ?? selectedAgentFromStorage

  const {
    agentSlug,
    vendorFilters,
    selectedModelId: selectedAgentModelId,
    setSelectedModelId: setSelectedAgentModelId,
  } = useAgentVendorModel(selectedAgent)

  // Only render when feature flag is enabled, user has Pro/Pro+ plan, and models are available
  const shouldShowModelPicker =
    copilotFeatureFlags.codingAgentModelSelection &&
    (copilotFeatureFlags.codingAgentModelSelectionAllSkus ||
      plan === CopilotPlan.IndividualPro ||
      plan === CopilotPlan.IndividualProPlus ||
      plan === CopilotPlan.IndividualMax ||
      plan === CopilotPlan.IndividualStudent) &&
    agentSupportsModelSelection(selectedAgent, agentSlug)

  if (!shouldShowModelPicker || !agentModels || agentModels.length === 0) {
    return null
  }

  return (
    <AgentModelPicker
      onUpdateModel={(modelId: string) => {
        setSelectedAgentModelId(modelId)
      }}
      selectedModelId={selectedAgentModelId}
      vendorFilters={vendorFilters}
      isTokenBasedBillingEnabled={tokenBasedBillingEnabled}
    />
  )
}
